import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Package, Store, Sparkles, ChevronDown, Trash2, X, Star, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { decidirMarcaNueva, MOTIVO_SOLO_ADMIN } from '../../lib/onboardingMarca/marcaNueva';
import { useMarcaNuevaSoloAdmin } from '../../hooks/useMarcaNuevaSoloAdmin';
import { useUltimoSaldo } from '../../hooks/useSaldo';
import { eliminarMarca } from '../../lib/eliminarMarca';
import { leerBilleterasDeMarcas } from '../../lib/saldo';
import { mensajeDeErrorAlBorrarMarca, motivoDeBorradoBloqueado } from '../../components/facturacionPorMarca/planesPorMarca';
import { isPaidPlan } from '../../lib/planCapabilities';
import { leerLugarParaMarcas, textoDeMarcasUsadas, useLugarParaMarcas } from '../../lib/lugarParaMarcas';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import { ETIQUETA_SIN_RUBRO, rubroVisible } from '../../lib/onboardingPendiente';
import { logoDeBranding } from '../../branding';

interface BrandInfo {
  id: string;
  name: string;
  rubro: string | null;
  logo_url: string | null;
  is_default: boolean;
  productCount: number;
}

const optimizedLogo = (url: string | null): string => {
  if (!url) return logoDeBranding('solo');
  return url.includes('supabase.co') ? `${url}?width=80&height=80` : url;
};

/**
 * Sección "Mis marcas" de Configuración: enfocada en VER y AÑADIR marcas (con
 * algo de info de cada una — rubro y nº de productos) + una explicación de para
 * qué sirve la función multi-marca. La edición de identidad (logo, colores,
 * tipografía, etc.) vive ahora en "Mi marca → Mi diseño".
 */
export const MisMarcasSection = ({ profile }: { profile: any }) => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<BrandInfo[]>([]);
  const lugarParaMarcas = useLugarParaMarcas(true, brands.length);
  const [loading, setLoading] = useState(true);
  const [showMultiBrandModal, setShowMultiBrandModal] = useState(false);
  // Un plan pago que ya usó sus marcas (ADR-2026-10-27-planes-de-equipo-en-el-front).
  const [topeDeMarcasAlcanzado, setTopeDeMarcasAlcanzado] = useState<number | null | undefined>(undefined);
  // La explicación arranca abierta para quien todavía no usa multi-marca; si ya
  // tiene más de una marca, arranca colapsada (mostrando solo la pregunta).
  const [explanationOpen, setExplanationOpen] = useState(true);
  // Eliminar marca: marca apuntada para borrar (abre el modal) + estado in-flight.
  const [deleteTarget, setDeleteTarget] = useState<BrandInfo | null>(null);
  // Con facturación por marca cada marca paga su plan y sólo el dueño las suma
  // (ADR-2026-10-28-marca-nueva-con-pago).
  const facturaPorMarca = useUltimoSaldo()?.facturaPorMarca === true;
  const marcaNuevaSoloAdmin = useMarcaNuevaSoloAdmin();
  const deleteTargetRef = useRef<BrandInfo | null>(null);
  deleteTargetRef.current = deleteTarget;
  const [deleting, setDeleting] = useState(false);
  // Con facturación por marca: por qué esta marca no se puede borrar (tiene plan).
  const [motivoBloqueo, setMotivoBloqueo] = useState<string | null>(null);
  // Hacer principal: marca apuntada (abre el modal) + estado in-flight.
  const [makePrincipalTarget, setMakePrincipalTarget] = useState<BrandInfo | null>(null);
  const [makingPrincipal, setMakingPrincipal] = useState(false);
  // Feedback de error de las acciones (borrar / hacer principal); se muestra en el
  // modal correspondiente en vez de fallar en silencio.
  const [actionError, setActionError] = useState<string | null>(null);

  const loadBrands = useCallback(async () => {
    if (!profile?.id_company) return;
    setLoading(true);
    try {
      const { data: brandsData } = await supabase
        .from('brand_profiles')
        .select('id, name, rubro, logo_url, is_default')
        .eq('id_company', profile.id_company)
        .eq('is_draft', false)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });
      const list = (brandsData || []) as Omit<BrandInfo, 'productCount'>[];

      // Conteo de productos por marca (un solo query, agrupado en cliente).
      const ids = list.map((b) => b.id);
      const counts: Record<string, number> = {};
      if (ids.length > 0) {
        const { data: prods } = await supabase
          .from('productos')
          .select('id, brand_id')
          .in('brand_id', ids);
        (prods || []).forEach((p: any) => {
          if (p.brand_id) counts[p.brand_id] = (counts[p.brand_id] || 0) + 1;
        });
      }

      setBrands(list.map((b) => ({ ...b, productCount: counts[b.id] || 0 })));
      setExplanationOpen(list.length <= 1);
    } catch {
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id_company]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Eliminar la marca apuntada. La principal nunca es eliminable. Si la marca
  // borrada era la activa del usuario (`current_brand_id`), reasigna a la
  // principal y avisa al resto de la app vía `atlas:brand-changed`.
  const handleDeleteBrand = async () => {
    if (!deleteTarget || deleteTarget.is_default) return;
    setDeleting(true);
    setActionError(null);
    try {
      // Con facturación por marca, una marca con plan no se borra (lee la billetera fresca).
      if (facturaPorMarca) {
        const b = (await leerBilleterasDeMarcas([deleteTarget.id])).get(deleteTarget.id);
        const motivo = motivoDeBorradoBloqueado(b);
        if (motivo) {
          setMotivoBloqueo(motivo);
          setActionError(motivo);
          return;
        }
      }
      // Reasignar los productos de la marca a la PRINCIPAL ANTES de borrar. Si
      // quedaran en null (como hacía el borrado antes), se colarían bajo TODAS
      // las marcas por el scoping `brand_id = activa OR brand_id IS NULL`.
      // Movidos a la principal quedan prolijos bajo ella.
      const principal = brands.find((b) => b.is_default);
      // Sin principal (empresa sin marca default) abortamos SIN borrar: dejar los
      // productos en null los orfanaría (justo lo que este flujo quiere evitar).
      // La FK `ON DELETE SET NULL` los nulificaría al borrar la marca.
      if (!principal) {
        throw new Error(
          'No hay una marca principal a la que reasignar los productos. Marcá una como principal e intentá de nuevo.',
        );
      }
      await eliminarMarca(deleteTarget.id, principal.id);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        if ((prof as any)?.current_brand_id === deleteTarget.id) {
          const fallback = brands.find((b) => b.is_default && b.id !== deleteTarget.id);
          if (fallback) {
            await supabase
              .from('profiles')
              .update({ current_brand_id: fallback.id })
              .eq('id', user.id);
            window.dispatchEvent(
              new CustomEvent('atlas:brand-changed', { detail: { brandId: fallback.id } }),
            );
          }
        }
      }

      setDeleteTarget(null);
      await loadBrands();
    } catch (e) {
      // Mostramos el error en el modal (queda abierto) y refrescamos para reflejar
      // el estado real de la DB por si algún paso sí se aplicó.
      setActionError(facturaPorMarca ? mensajeDeErrorAlBorrarMarca(e) : e instanceof Error ? e.message : 'No se pudo eliminar la marca. Probá de nuevo.');
      await loadBrands();
    } finally {
      setDeleting(false);
    }
  };

  // Cambiar la marca PRINCIPAL (is_default). Solo afecta el "por defecto" para
  // productos nuevos y el respaldo al borrar la activa; NO cambia la marca activa
  // (current_brand_id), que se sigue eligiendo desde "Mi marca".
  const handleMakePrincipal = async () => {
    if (!makePrincipalTarget || makePrincipalTarget.is_default) return;
    if (!profile?.id_company) return;
    const target = makePrincipalTarget;
    const prevDefault = brands.find((b) => b.is_default) || null;
    setMakingPrincipal(true);
    setActionError(null);
    try {
      // La base tiene un índice único parcial ("una sola principal por empresa"),
      // así que NO puede haber 2 defaults ni por un instante. Por eso apagamos la
      // principal actual PRIMERO y recién después prendemos la nueva (el orden
      // inverso rebota con violación de unicidad).
      const { error: eOff } = await supabase
        .from('brand_profiles')
        .update({ is_default: false })
        .eq('id_company', profile.id_company)
        .eq('is_default', true);
      if (eOff) throw eOff;

      const { error: eOn } = await supabase
        .from('brand_profiles')
        .update({ is_default: true })
        .eq('id', target.id);
      if (eOn) {
        // Falló prender la nueva: restauramos la anterior para no quedar sin
        // principal.
        if (prevDefault) {
          await supabase
            .from('brand_profiles')
            .update({ is_default: true })
            .eq('id', prevDefault.id);
        }
        throw eOn;
      }

      setMakePrincipalTarget(null);
      await loadBrands();
    } catch {
      // Mostramos el error en el modal (queda abierto) y refrescamos para reflejar
      // el estado real de la DB (por si quedó a mitad de camino).
      setActionError('No se pudo cambiar la marca principal. Probá de nuevo.');
      await loadBrands();
    } finally {
      setMakingPrincipal(false);
    }
  };

  const handleAddBrand = async () => {
    const decision = await decidirMarcaNueva();
    if (decision === 'solo_admin') return;
    // Con facturación por marca no hay tope: cada marca paga su plan
    // (ADR-2026-10-28-facturacion-por-marca-sin-tope-de-marcas).
    const lugar = await leerLugarParaMarcas();
    if (!lugar.puedeSumar) {
      if (isPaidPlan(lugar.plan)) setTopeDeMarcasAlcanzado(lugar.tope);
      else setShowMultiBrandModal(true);
      return;
    }
    navigate('/redes-sociales/descripcion-brand?mode=additional');
  };

  return (
    <div className="space-y-4">
      {/* Título — solo desktop (en mobile lo muestra el header de Configuración) */}
      {/* "Marcas", sin el posesivo, igual que el ítem del menú
          (ADR-2026-08-04-la-seccion-de-cuenta-se-llama-informacion): el menú y el
          título de la derecha tienen que decir lo mismo. */}
      <h2 className="hidden sm:block text-[18px] font-semibold text-brand-black/90">
        Marcas
      </h2>

      {/* Explicación de la función multi-marca. Colapsable: si el usuario ya
          tiene más de una marca arranca cerrada (solo la pregunta) y se abre al
          tocarla. */}
      <div className="rounded-lg bg-brand-primary/[0.04] border border-brand-primary/15">
        <button
          type="button"
          onClick={() => setExplanationOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 p-4 text-left"
        >
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-primary shrink-0" />
            <span className="text-[14px] font-semibold text-brand-black/90">
              ¿Para qué sirven las marcas?
            </span>
          </span>
          <ChevronDown
            className={`w-4 h-4 text-brand-primary shrink-0 transition-transform ${explanationOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {explanationOpen && (
          <div className="px-4 pb-4">
            <p className="text-[13px] text-brand-black/70 leading-relaxed">
              Las marcas te dejan manejar varios negocios o líneas de producto
              desde una misma cuenta. Cada marca tiene su propia identidad (logo,
              colores, tipografía y rubro) y sus propios productos.
            </p>
            <p className="text-[13px] text-brand-black/70 leading-relaxed mt-2">
              <span className="font-medium text-brand-black/90">Cómo funciona:</span>{' '}
              al generar imágenes o videos, Atlas usa la información de la marca
              activa, así el contenido sale siempre con el estilo correcto. Podés
              cambiar de marca activa cuando quieras desde <span className="font-medium">Mi marca</span>,
              y editar la identidad de cada una en <span className="font-medium">Mi marca → Mi diseño</span>.
            </p>
            <p className="text-[13px] font-medium text-brand-black/90 leading-relaxed mt-2">
              {facturaPorMarca
                ? 'Cada marca tiene su propio plan: al sumar una, elegís el suyo.'
                : 'Agregar marcas no tiene costo extra: están incluidas en tu plan.'}
            </p>
          </div>
        )}
      </div>

      {/* Lista de marcas */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      ) : (
        <div className="space-y-2.5">
          {brands.map((b) => (
            <div
              key={b.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 bg-surface-elevated"
            >
              <img
                src={optimizedLogo(b.logo_url)}
                alt=""
                className="w-11 h-11 rounded-md object-contain bg-white border border-neutral-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = logoDeBranding('solo');
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-brand-black/90 truncate">
                    {b.name}
                  </span>
                  {b.is_default && (
                    <span className="shrink-0 text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                      Principal
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[12.5px] text-neutral-500">
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <Store className="w-3.5 h-3.5 shrink-0" />
                    {/* El centinela del alta por link no es un rubro: es que todavía no
                        se preguntó, o sea exactamente lo que este "Sin rubro" ya decía
                        para una marca sin cargar (`lib/onboardingPendiente`). */}
                    <span className="truncate">{rubroVisible(b.rubro) ?? ETIQUETA_SIN_RUBRO}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <Package className="w-3.5 h-3.5" />
                    {b.productCount} {b.productCount === 1 ? 'producto' : 'productos'}
                  </span>
                </div>
              </div>

              {/* Acciones — solo en marcas no principales. La principal no se
                  puede hacer principal (ya lo es) ni borrar. */}
              {!b.is_default && (
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setMakePrincipalTarget(b);
                  }}
                  aria-label={`Hacer principal ${b.name}`}
                  title="Hacer principal"
                  className="shrink-0 h-9 px-2.5 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 text-[12px] font-medium text-neutral-600 hover:text-brand-primary hover:border-brand-primary/30 hover:bg-brand-primary/[0.04] transition-colors"
                >
                  <Star className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden sm:inline">Hacer principal</span>
                </button>
              )}
              {!b.is_default && (
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setMotivoBloqueo(null);
                    setDeleteTarget(b);
                    deleteTargetRef.current = b;
                    if (facturaPorMarca) {
                      void leerBilleterasDeMarcas([b.id])
                        .then((m) => {
                          // Un resultado tardío de otra marca no pisa al actual.
                          if (deleteTargetRef.current?.id === b.id) setMotivoBloqueo(motivoDeBorradoBloqueado(m.get(b.id)));
                        })
                        .catch(() => {});
                    }
                  }}
                  aria-label={`Eliminar ${b.name}`}
                  title="Eliminar marca"
                  className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md border border-neutral-200 text-neutral-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}

          {/* Añadir nueva marca — debajo de la lista */}
          <button
            type="button"
            onClick={handleAddBrand}
            disabled={marcaNuevaSoloAdmin}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-neutral-300 text-neutral-500 hover:border-brand-primary/40 hover:text-brand-primary hover:bg-brand-primary/[0.03] transition-colors text-[14px] font-medium disabled:opacity-60 disabled:hover:border-neutral-300 disabled:hover:text-neutral-500 disabled:hover:bg-transparent"
          >
            {lugarParaMarcas && !lugarParaMarcas.puedeSumar ? (
              <Lock className="w-4 h-4" aria-label="Sin lugar en tu plan" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Añadir nueva marca
            {textoDeMarcasUsadas(lugarParaMarcas) && (
              <span className="font-normal text-neutral-400">· {textoDeMarcasUsadas(lugarParaMarcas)}</span>
            )}
          </button>
          {marcaNuevaSoloAdmin && (
            <p className="text-center text-[12.5px] text-neutral-500">{MOTIVO_SOLO_ADMIN}</p>
          )}
        </div>
      )}

      {/* Modal de confirmación de eliminación de marca */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Eliminar marca</h3>
              <p className="text-[13px] text-neutral-500">
                Vas a eliminar la marca <strong>{deleteTarget.name}</strong>. Se perderá su
                identidad visual y su análisis, y los productos vinculados pasarán a tu
                marca principal. Esta acción no se puede deshacer.
              </p>
            </div>

            {(actionError || motivoBloqueo) && (
              <p className="text-[13px] text-red-600 text-center mb-4">{actionError ?? motivoBloqueo}</p>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBrand}
                disabled={deleting || !!motivoBloqueo}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar marca'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de "Hacer principal" */}
      {makePrincipalTarget && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !makingPrincipal && setMakePrincipalTarget(null)}
        >
          <div
            className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setMakePrincipalTarget(null)}
              disabled={makingPrincipal}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mb-3">
                <Star className="w-5 h-5 text-brand-primary" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Hacer principal</h3>
              <p className="text-[13px] text-neutral-500">
                <strong>{makePrincipalTarget.name}</strong> pasará a ser tu marca
                principal: se usará por defecto para los productos nuevos y como
                respaldo. Tu marca activa no cambia — la seguís eligiendo desde{' '}
                <span className="font-medium">Mi marca</span>.
              </p>
            </div>

            {actionError && (
              <p className="text-[13px] text-red-600 text-center mb-4">{actionError}</p>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setMakePrincipalTarget(null)}
                disabled={makingPrincipal}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleMakePrincipal}
                disabled={makingPrincipal}
                className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {makingPrincipal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Hacer principal'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showMultiBrandModal}
        onClose={() => setShowMultiBrandModal(false)}
        type="multi-brand-required"
      />
      <SubscriptionLimitModal
        isOpen={topeDeMarcasAlcanzado !== undefined}
        onClose={() => setTopeDeMarcasAlcanzado(undefined)}
        type="tope-de-marcas"
        tope={topeDeMarcasAlcanzado}
      />
    </div>
  );
};
